import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { estimateService } from '../../../services/estimateService';
import { CustomerService } from '../../../services/customerService';
import { apiClient } from '../../../services/api';
import { productService } from '../../../services/productService';
import { warehouseService } from '../../../services/warehouseService';
import { customerAddressService, CustomerAddress } from '../../../services/customerAddressService';
import { CustomerQuickAddDialog } from '../../../components/customers/CustomerQuickAddDialog';
import { EstimateCreate, EstimateUpdate, EstimateItem } from '../../../types/estimate';
import { formatCurrency } from '../../../utils/formatters';

interface EstimateFormData extends Omit<EstimateCreate, 'items'> {
  items: EstimateItem[];
}

const EstimateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(id);

  // Form state
  const [formData, setFormData] = useState<EstimateFormData>({
    customer_id: '',
    estimate_date: format(new Date(), 'yyyy-MM-dd'),
    valid_until: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 30 days from now
    billing_address_id: '',
    shipping_address_id: '',
    notes: '',
    terms_and_conditions: '',
    items: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerAddresses, setCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [estimate, setEstimate] = useState<any>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [customersData, setCustomersData] = useState<any>(null);
  const [productsData, setProductsData] = useState<any>(null);
  const [stockInfo, setStockInfo] = useState<Record<string, any>>({});

  // Warehouse state - persisted in localStorage
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>(() => {
    return localStorage.getItem('admin_selectedWarehouseId') || '';
  });

  // Quick Customer Creation State
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [customerGroups, setCustomerGroups] = useState<any[]>([]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch customers
        const customers = await CustomerService.getCustomers({ page: 1, per_page: 100 });
        setCustomersData(customers);

        // Fetch customer groups for quick creation
        const groups = await CustomerService.getCustomerGroups({ page: 1, per_page: 50, is_active: true });
        setCustomerGroups(groups.items || []);

        // Fetch warehouses
        const whData = await warehouseService.getWarehouses();
        setWarehouses(whData);
        if (!localStorage.getItem('admin_selectedWarehouseId') && whData.length > 0) {
          setSelectedWarehouseId(whData[0].id);
          localStorage.setItem('admin_selectedWarehouseId', whData[0].id);
        }

        // Fetch products
        const products = await productService.listProducts({ page: 1, per_page: 1000 });
        console.log('📦 Products API Response:', products);
        console.log('📦 Total products:', products?.items?.length);
        
        // Find products with prices
        const productsWithPrices = products?.items?.filter((p: any) => 
          p.variants?.some((v: any) => v.sale_price || v.mrp)
        );
        console.log('📦 Products WITH prices:', productsWithPrices?.length);
        
        // Find products without prices
        const productsWithoutPrices = products?.items?.filter((p: any) => 
          !p.variants?.some((v: any) => v.sale_price || v.mrp)
        );
        console.log('📦 Products WITHOUT prices:', productsWithoutPrices?.length);
        
        // Log a product with prices
        const productWithPrice = productsWithPrices?.[0];
        if (productWithPrice) {
          console.log('📦 Sample product WITH price:', productWithPrice.name);
          console.log('📦 Its variant:', productWithPrice.variants[0]);
          console.log('📦 Variant pricing:', {
            mrp: productWithPrice.variants[0]?.mrp,
            sale_price: productWithPrice.variants[0]?.sale_price,
            wholesale_price: productWithPrice.variants[0]?.wholesale_price
          });
        }
        
        // Search for "10 IN 1 MINI PEN"
        const miniPen = products?.items?.find((p: any) => 
          p.name?.toLowerCase().includes('10 in 1 mini pen')
        );
        if (miniPen) {
          console.log('📦 Found "10 IN 1 MINI PEN":', miniPen);
          console.log('📦 Its variants:', miniPen.variants);
        } else {
          console.log('❌ "10 IN 1 MINI PEN" not found in API response');
        }
        
        setProductsData(products);

        // Fetch existing estimate if editing
        if (isEditing && id) {
          setEstimateLoading(true);
          const estimateData = await estimateService.getEstimate(id);
          setEstimate(estimateData);
          setFormData({
            customer_id: estimateData.customer_id,
            estimate_date: estimateData.estimate_date,
            valid_until: estimateData.valid_until,
            billing_address_id: estimateData.billing_address_id,
            shipping_address_id: estimateData.shipping_address_id,
            notes: estimateData.notes || '',
            terms_and_conditions: estimateData.terms_and_conditions || '',
            items: estimateData.items
          });
          setEstimateLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      }
    };

    fetchData();
  }, [id, isEditing, enqueueSnackbar]);

  // Load customer addresses when customer is selected
  useEffect(() => {
    const loadCustomerAddresses = async () => {
      if (formData.customer_id && customersData?.items) {
        const customer = customersData.items.find((c: any) => c.id === formData.customer_id);
        if (customer) {
          setSelectedCustomer(customer);
          setAddressesLoading(true);
          
          try {
            const addressResponse = await customerAddressService.getCustomerAddresses(formData.customer_id);
            setCustomerAddresses(addressResponse.addresses);
            
            // Auto-select default addresses if not already set
            if (!formData.billing_address_id) {
              const defaultBilling = addressResponse.addresses.find(
                addr => (addr.address_type === 'billing' || addr.address_type === 'both') && addr.is_default
              );
              if (defaultBilling) {
                setFormData(prev => ({ ...prev, billing_address_id: defaultBilling.id }));
              }
            }
            
            if (!formData.shipping_address_id) {
              const defaultShipping = addressResponse.addresses.find(
                addr => (addr.address_type === 'shipping' || addr.address_type === 'both') && addr.is_default
              );
              if (defaultShipping) {
                setFormData(prev => ({ ...prev, shipping_address_id: defaultShipping.id }));
              }
            }
          } catch (error) {
            console.error('Failed to load customer addresses:', error);
            enqueueSnackbar('Failed to load customer addresses', { variant: 'error' });
          } finally {
            setAddressesLoading(false);
          }
        }
      } else {
        setCustomerAddresses([]);
        setSelectedCustomer(null);
      }
    };

    loadCustomerAddresses();
  }, [formData.customer_id, customersData, enqueueSnackbar]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validate form
      const validationErrors: Record<string, string> = {};
      
      if (!formData.customer_id) {
        validationErrors.customer_id = 'Customer is required';
      }
      
      if (!formData.items.length) {
        validationErrors.items = 'At least one item is required';
      } else {
        // Validate each item
        formData.items.forEach((item, index) => {
          if (!item.variant_id) {
            validationErrors[`item_${index}_variant`] = 'Product is required';
          }
          const qty = Number(item.quantity);
          if (isNaN(qty) || qty <= 0) {
            validationErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
          }
          const price = Number(item.unit_price);
          if (isNaN(price) || price < 0.01) {
            validationErrors[`item_${index}_price`] = 'Unit price must be at least ₹0.01';
          }
        });
      }
      
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        enqueueSnackbar('Please fix the validation errors', { variant: 'error' });
        return;
      }

      // Ensure all numeric fields in items are valid numbers
      const cleanedItems = formData.items.map(item => {
        const quantity = Number(item.quantity);
        const unitPrice = Number(item.unit_price);
        const discountPercent = Number(item.discount_percent);
        
        return {
          ...item,
          quantity: isNaN(quantity) || quantity <= 0 ? 1 : quantity,
          unit_price: isNaN(unitPrice) || unitPrice < 0 ? 0 : unitPrice,
          discount_percent: isNaN(discountPercent) || discountPercent < 0 ? 0 : (discountPercent > 100 ? 100 : discountPercent)
        };
      });

      const cleanedFormData = {
        ...formData,
        billing_address_id: formData.billing_address_id && formData.billing_address_id.trim() !== '' ? formData.billing_address_id : undefined,
        shipping_address_id: formData.shipping_address_id && formData.shipping_address_id.trim() !== '' ? formData.shipping_address_id : undefined,
        items: cleanedItems
      };

      if (isEditing) {
        await estimateService.updateEstimate(id!, cleanedFormData as EstimateUpdate);
        enqueueSnackbar('Estimate updated successfully', { variant: 'success' });
      } else {
        console.log('Submitting estimate with cleaned data:', cleanedFormData);
        const result = await estimateService.createEstimate(cleanedFormData);
        enqueueSnackbar('Estimate created successfully', { variant: 'success' });
        navigate(`/admin/documents/estimates/${result.id}`);
      }
    } catch (error: any) {
      console.error('Failed to save estimate:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save estimate';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch stock info for a variant (warehouse-filtered)
  const fetchStockInfo = async (variantId: string, warehouseId?: string) => {
    try {
      const wh = warehouseId || selectedWarehouseId;
      const params: Record<string, any> = { search: variantId };
      if (wh) params.warehouse_id = wh;
      
      const response = await apiClient.get('/admin/inventory/stock', { params });
      const data = response.data;
      if (data.items && data.items.length > 0) {
        const stock = data.items[0];
        setStockInfo(prev => ({
          ...prev,
          [variantId]: {
            current_stock: stock.current_stock,
            reserved_stock: stock.reserved_stock,
            available_stock: stock.available_stock,
            is_low_stock: stock.is_low_stock
          }
        }));
      } else {
        setStockInfo(prev => ({
          ...prev,
          [variantId]: { current_stock: 0, reserved_stock: 0, available_stock: 0, is_low_stock: true }
        }));
      }
    } catch (error) {
      console.error('Failed to fetch stock info:', error);
    }
  };

  // Refetch stock when warehouse changes
  useEffect(() => {
    if (selectedWarehouseId && formData.items.length > 0) {
      formData.items.forEach(item => {
        if (item.variant_id) fetchStockInfo(item.variant_id, selectedWarehouseId);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouseId]);

  // Handle newly quick-created customer (from CustomerQuickAddDialog)
  const handleQuickCustomerCreated = async (created: any) => {
    // Refresh customers list so the new customer appears in the picker
    const customers = await CustomerService.getCustomers({ page: 1, per_page: 100 });
    setCustomersData(customers);

    // Select the new customer
    setSelectedCustomer(created);
    setFormData(prev => ({ ...prev, customer_id: created.id }));
    setOpenCustomerDialog(false);
  };

  // Handle adding new line item
  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          variant_id: '',
          quantity: 1,
          unit_price: 0,
          discount_percent: 0,
          description: '',
          hsn_code: ''
        }
      ]
    }));
  };

  // Handle removing line item
  const removeLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Handle line item changes
  const updateLineItem = (index: number, field: keyof EstimateItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
    
    // Clear item-specific errors when user makes changes
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`item_${index}_variant`];
      delete newErrors[`item_${index}_quantity`];
      delete newErrors[`item_${index}_price`];
      return newErrors;
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unit_price) || 0;
      const discountPercent = Number(item.discount_percent) || 0;
      const lineTotal = qty * price;
      const discountAmount = lineTotal * (discountPercent / 100);
      return sum + (lineTotal - discountAmount);
    }, 0);

    const taxAmount = subtotal * 0.18; // 18% GST
    const total = subtotal + taxAmount;

    return { subtotal, taxAmount, total };
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  // Clear errors when form data changes
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const newErrors = { ...errors };
      
      // Clear customer error if customer is selected
      if (formData.customer_id && newErrors.customer_id) {
        delete newErrors.customer_id;
      }
      
      // Clear address errors if addresses are selected
      if (formData.billing_address_id && newErrors.billing_address_id) {
        delete newErrors.billing_address_id;
      }
      
      if (formData.shipping_address_id && newErrors.shipping_address_id) {
        delete newErrors.shipping_address_id;
      }
      
      // Clear items error if items exist
      if (formData.items.length > 0 && newErrors.items) {
        delete newErrors.items;
      }
      
      setErrors(newErrors);
    }
  }, [formData.customer_id, formData.billing_address_id, formData.shipping_address_id, formData.items.length, errors]);

  if (estimateLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/estimates">
          Estimates
        </Link>
        <Typography color="text.primary">
          {isEditing ? 'Edit Estimate' : 'New Estimate'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/estimates')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            {isEditing ? `Edit Estimate ${estimate?.estimate_number}` : 'Create New Estimate'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Estimate'}
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={1.5}>
          {/* Customer Information - Single Row */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={1.5} alignItems="center">
                  {/* Warehouse Selector Banner */}
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1.5}
                      sx={{
                        bgcolor: selectedWarehouseId ? 'primary.50' : 'warning.50',
                        border: '1px solid',
                        borderColor: selectedWarehouseId ? 'primary.200' : 'warning.300',
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} color={selectedWarehouseId ? 'primary.main' : 'warning.dark'} sx={{ whiteSpace: 'nowrap' }}>
                        🏭 Dispatch Warehouse:
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <Select
                          value={selectedWarehouseId}
                          onChange={(e) => {
                            const wid = e.target.value;
                            setSelectedWarehouseId(wid);
                            localStorage.setItem('admin_selectedWarehouseId', wid);
                            setStockInfo({});
                            formData.items.forEach(item => {
                              if (item.variant_id) fetchStockInfo(item.variant_id, wid);
                            });
                          }}
                          displayEmpty
                          sx={{ bgcolor: 'background.paper' }}
                        >
                          <MenuItem value=""><em>All Warehouses</em></MenuItem>
                          {warehouses.map((wh) => (
                            <MenuItem key={wh.id} value={wh.id}>
                              {wh.name}{wh.city ? ` — ${wh.city}` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {!selectedWarehouseId && (
                        <Typography variant="caption" color="warning.dark">
                          ⚠ Select a warehouse to see accurate per-warehouse stock
                        </Typography>
                      )}
                      {selectedWarehouseId && warehouses.find(w => w.id === selectedWarehouseId) && (
                        <Typography variant="caption" color="text.secondary">
                          Stock shown is for {warehouses.find(w => w.id === selectedWarehouseId)?.name}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" gap={1} alignItems="flex-start">
                      <Autocomplete
                        fullWidth
                        options={customersData?.items || []}
                        getOptionLabel={(option: any) => {
                          // Try multiple fields to find a display name
                          return option.business_name ||
                                 option.billing_name ||
                                 option.contact_person ||
                                 option.email ||
                                 option.mobile ||
                                 'Unnamed Customer';
                        }}
                        getOptionKey={(option: any) => option.id}
                        value={selectedCustomer}
                        onChange={(_, newValue) => {
                          console.log('[EstimateForm] Customer selected:', newValue);
                          setSelectedCustomer(newValue);
                          setFormData(prev => ({
                            ...prev,
                            customer_id: newValue?.id || ''
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Customer"
                            size="small"
                            required
                            error={!!errors.customer_id}
                            helperText={errors.customer_id}
                            placeholder={customersData?.items?.length ? "Select customer" : "Loading customers..."}
                          />
                        )}
                      />
                      <Tooltip title="Add New Customer">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => setOpenCustomerDialog(true)}
                          sx={{
                            border: '1px solid',
                            borderColor: 'primary.main',
                            flexShrink: 0,
                            mt: 0.5
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {/* Debug info */}
                    {process.env.NODE_ENV === 'development' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {customersData ? `${customersData.items?.length || 0} customers loaded` : 'No data'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Estimate Date"
                      type="date"
                      value={formData.estimate_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, estimate_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Valid Until"
                      type="date"
                      value={formData.valid_until}
                      onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        billing_address_id: '', 
                        shipping_address_id: '' 
                      }))}
                    >
                      Clear Addresses
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Addresses - Collapsible/Optional */}
          {formData.customer_id && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Billing Address (Optional)</InputLabel>
                        <Select
                          value={formData.billing_address_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, billing_address_id: e.target.value }))}
                          label="Billing Address (Optional)"
                          disabled={addressesLoading}
                        >
                          <MenuItem value="">None</MenuItem>
                          {customerAddresses.filter(addr => addr.address_type === 'billing' || addr.address_type === 'both').map((address) => (
                            <MenuItem key={address.id} value={address.id}>
                              {address.address_line1}, {address.city}, {address.state} - {address.postal_code}
                              {address.is_default && ' (Default)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Shipping Address (Optional)</InputLabel>
                        <Select
                          value={formData.shipping_address_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, shipping_address_id: e.target.value }))}
                          label="Shipping Address (Optional)"
                          disabled={addressesLoading}
                        >
                          <MenuItem value="">None</MenuItem>
                          {customerAddresses.filter(addr => addr.address_type === 'shipping' || addr.address_type === 'both').map((address) => (
                            <MenuItem key={address.id} value={address.id}>
                              {address.address_line1}, {address.city}, {address.state} - {address.postal_code}
                              {address.is_default && ' (Default)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Line Items */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Line Items ({formData.items.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addLineItem}
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Add
                  </Button>
                </Box>

                {errors.items && (
                  <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                    {errors.items}
                  </Alert>
                )}

                <TableContainer sx={{ maxHeight: '450px', overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '40px' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', minWidth: '200px' }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '80px' }}>MRP</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '80px' }}>
                          Stock{selectedWarehouseId && warehouses.find(w => w.id === selectedWarehouseId) ? ` (${warehouses.find(w => w.id === selectedWarehouseId)?.name.split(' ')[0]})` : ''}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Unit Price</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Disc %</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Taxable</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Tax %</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Tax Amt</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '100px' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '50px' }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => {
                        const qty = Number(item.quantity) || 0;
                        const price = Number(item.unit_price) || 0;
                        const discountPercent = Number(item.discount_percent) || 0;
                        
                        const lineTotal = qty * price;
                        const discountAmount = lineTotal * (discountPercent / 100);
                        const taxableAmount = lineTotal - discountAmount;
                        const taxRate = 18; // Default GST rate
                        const taxAmount = taxableAmount * (taxRate / 100);
                        const finalAmount = taxableAmount + taxAmount;
                        
                        // Get selected variant to show MRP — include product-level B2B fields
                        const selectedVariant = productsData?.items?.flatMap((p: any) => 
                          (p.variants || []).map((v: any) => ({
                            ...v,
                            product_name: p.name,
                            product_id: p.id,
                            is_set_selling: p.is_set_selling || false,
                            set_size: p.set_size || 1,
                            moq: p.moq || 1,
                            carton_quantity: p.carton_quantity || null,
                            inner_box_quantity: p.inner_box_quantity || null,
                            product_unit: p.product_unit || 'pcs',
                          }))
                        ).find((v: any) => v.id === item.variant_id);

                        return (
                          <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {index + 1}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Autocomplete
                                options={productsData?.items?.flatMap((p: any) => 
                                  (p.variants || []).map((v: any) => ({
                                    ...v,
                                    product_name: p.name,
                                    product_id: p.id,
                                    hsn_code: p.hsn_code,
                                    is_set_selling: p.is_set_selling || false,
                                    set_size: p.set_size || 1,
                                    moq: p.moq || 1,
                                    carton_quantity: p.carton_quantity || null,
                                    inner_box_quantity: p.inner_box_quantity || null,
                                    product_unit: p.product_unit || 'pcs',
                                  }))
                                ) || []}
                                getOptionLabel={(option: any) => `${option.product_name || 'Unknown'} - ${option.name}`}
                                getOptionKey={(option: any) => option.id}
                                value={selectedVariant || null}
                                onChange={(_, newValue) => {
                                  if (newValue) {
                                    const basePrice = Number(newValue.sale_price || newValue.mrp || 0);
                                    const sz = newValue.set_size || 1;
                                    const unitPrice = newValue.is_set_selling ? basePrice * sz : basePrice;
                                    const initialQty = newValue.moq || 1;
                                    
                                    // Fetch stock info for the selected warehouse
                                    fetchStockInfo(newValue.id, selectedWarehouseId);
                                    
                                    setFormData(prev => ({
                                      ...prev,
                                      items: prev.items.map((item, i) => 
                                        i === index ? {
                                          ...item,
                                          variant_id: newValue.id,
                                          unit_price: unitPrice,
                                          quantity: initialQty,
                                          hsn_code: newValue.hsn_code || '',
                                          discount_percent: Number(item.discount_percent) || 0
                                        } : item
                                      )
                                    }));
                                  } else {
                                    updateLineItem(index, 'variant_id', '');
                                    updateLineItem(index, 'unit_price', 0);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select product"
                                    error={!!errors[`item_${index}_variant`]}
                                    sx={{ 
                                      minWidth: 200,
                                      '& .MuiInputBase-root': { fontSize: '0.8125rem', py: 0.25 },
                                      '& .MuiInputBase-input': { py: '4px !important' }
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {selectedVariant?.mrp ? `₹${Number(selectedVariant.mrp).toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {selectedVariant ? (
                                <Tooltip title={`Total: ${selectedVariant.total_stock ?? stockInfo[item.variant_id]?.current_stock ?? 0} | Reserved: ${selectedVariant.total_reserved ?? stockInfo[item.variant_id]?.reserved_stock ?? 0}`}>
                                  <span>{selectedVariant.total_available ?? stockInfo[item.variant_id]?.available_stock ?? 0}</span>
                                </Tooltip>
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={item.quantity}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '' || /^\d+$/.test(val)) {
                                    updateLineItem(index, 'quantity', val === '' ? '' : parseInt(val));
                                  }
                                }}
                                sx={{ 
                                  width: 65,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                                error={!!errors[`item_${index}_quantity`]}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={item.unit_price}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                    updateLineItem(index, 'unit_price', val);
                                  }
                                }}
                                sx={{ 
                                  width: 85,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                                error={!!errors[`item_${index}_price`]}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={item.discount_percent}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                    updateLineItem(index, 'discount_percent', val);
                                  }
                                }}
                                sx={{ 
                                  width: 65,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', fontWeight: 500 }}>
                              ₹{taxableAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {taxRate}%
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', color: 'info.main' }}>
                              ₹{taxAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', fontWeight: 600, color: 'primary.main' }}>
                              ₹{finalAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeLineItem(index)}
                                disabled={formData.items.length === 1}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Calculation Preview */}
                <Box mt={2}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Calculation Preview
                  </Typography>
                  <Grid container spacing={1.5} justifyContent="flex-end">
                    <Grid item xs={12} md={5}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Subtotal (before discount):</Typography>
                            <Typography variant="body2">{formatCurrency(subtotal + formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price * (item.discount_percent / 100)), 0))}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Total Discount:</Typography>
                            <Typography variant="body2" color="success.main">
                              -{formatCurrency(formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price * (item.discount_percent / 100)), 0))}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Taxable Amount:</Typography>
                            <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">GST (18%):</Typography>
                            <Typography variant="body2">{formatCurrency(taxAmount)}</Typography>
                          </Box>
                          <Divider sx={{ my: 0.75 }} />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight={600} color="primary">
                              Total Amount:
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={600} color="primary">
                              {formatCurrency(total)}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Items: {formData.items.length} | Total Qty: {formData.items.reduce((sum, item) => sum + item.quantity, 0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Notes and Terms - Inline */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={2}
                      size="small"
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Terms and Conditions"
                      multiline
                      rows={2}
                      size="small"
                      value={formData.terms_and_conditions}
                      onChange={(e) => setFormData(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>

      {/* Quick Add Customer Dialog */}
      <CustomerQuickAddDialog
        open={openCustomerDialog}
        onClose={() => setOpenCustomerDialog(false)}
        customerGroups={customerGroups}
        onCreated={handleQuickCustomerCreated}
      />
    </Box>
  );
};

export default EstimateForm;